import { PACKAGE_LIST } from '@/constants/sideNavigation';
import ICON_SEND from '@/assets/sideBar/send.png';
import { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@successmode-ktp/kui';
import CompleteRequestWrapper from '@/components/sideNavigation/Cs/common/CompleteRequestWrapper';
import { useMutation } from 'react-query';
import { sendChat } from '@/api/cs';
import { useAppDispatch } from '@/hooks/useReduxHooks';
import { updateCs } from '@/store/modules/cs';
import { PackageAccept } from '@/types/cs.type';

type Props = {
  data?: PackageAccept;
};

function RequestPackageSection({ data }: Props) {
  const dispatch = useAppDispatch();

  const [packageNames, setPackageNames] = useState<string[]>([]);
  const [isEtcSelected, setIsEtcSelected] = useState(false);
  const [etc, setEtc] = useState('');

  const { mutate: mutateSendChat } = useMutation(sendChat);

  const onRequestPackage = () => {
    console.log('onRequestPackage', packageNames, etc, !!etc);
    mutateSendChat(
      {
        messageType: 'PACKAGE_ACCEPTED',
        packageNames,
        etc: isEtcSelected ? etc.trim() : '',
      },
      {
        onSuccess: (data) => {
          dispatch(
            updateCs({
              chatId: data.chatId,
            }),
          );
        },
      },
    );
  };

  const onSelectGoods = (value: string) => {
    if (packageNames.includes(value)) {
      setPackageNames((prev) => prev.filter((i) => i !== value));
    } else {
      setPackageNames((prev) => [...prev, value]);
    }
  };

  const isDisabledSendingButton = isEtcSelected
    ? etc === ''
    : packageNames.length === 0;

  if (data) {
    return (
      <CompleteRequestWrapper
        title='정상적으로 신청되었습니다'
        content={
          <>
            <ContentTitle>신청한 소모품</ContentTitle>
            <Selections>
              {data?.packageNames.map((packageName) => (
                <Item>{packageName}</Item>
              ))}
              {data?.etc && (
                <Item>
                  기타<span> | {data?.etc}</span>
                </Item>
              )}
            </Selections>
          </>
        }
      />
    );
  }

  return (
    <Body>
      <Content>
        <Description>
          <strong>필요한 소모품을 선택해 주세요</strong>
          <br />
          요청을 보내시면 병원으로 배송해드릴게요
        </Description>
        <GoodsList>
          {PACKAGE_LIST.map(({ key, label, value, image, width }) => (
            <GoodsItem
              key={key}
              onClick={() =>
                key === 'ETC'
                  ? setIsEtcSelected((prev) => !prev)
                  : onSelectGoods(value)
              }
            >
              <GoodsInfo>
                <GoodsImage>
                  <img src={image} alt={label} width={width} />
                </GoodsImage>
                <GoodsLabel>{label}</GoodsLabel>
              </GoodsInfo>
              <Checkbox
                isSelected={
                  key === 'ETC' ? isEtcSelected : packageNames.includes(value)
                }
              >
                <Icon.Check
                  fillColor={
                    (
                      key === 'ETC'
                        ? isEtcSelected
                        : packageNames.includes(value)
                    )
                      ? '#fff'
                      : 'transparent'
                  }
                  size={18}
                />
              </Checkbox>
            </GoodsItem>
          ))}
        </GoodsList>
        {isEtcSelected && (
          <EtcInput
            value={etc}
            onChange={(e) => setEtc(e.target.value)}
            placeholder='ex) 텍스리펀 영수증 포스트잇'
          />
        )}
      </Content>
      <Footer>
        <SendButton
          onClick={onRequestPackage}
          disabled={isDisabledSendingButton}
        >
          <img src={ICON_SEND} alt='' width={20} />
          보내기
        </SendButton>
      </Footer>
    </Body>
  );
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 2px 4px;
  width: 243px;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled.p`
  color: #3a3b3e;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 10px;

  strong {
    font-weight: 700;
  }
`;

const Footer = styled.footer`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const SendButton = styled.button`
  margin-top: 24px;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  border-radius: 8px;
  background-color: #246cf6;
  transition: all 200ms;

  &:disabled {
    background-color: #d3e2fd;
  }
`;

const GoodsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: flex-start;
`;

const GoodsItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
`;

const GoodsInfo = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: flex-start;
`;

const GoodsImage = styled.div`
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 100px;
`;

const GoodsLabel = styled.label`
  color: #3a3b3e;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
`;

const Checkbox = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${({ isSelected }) => (isSelected ? '#246CF6' : '#cbccce')};
  background-color: ${({ isSelected }) =>
    isSelected ? '#246CF6' : 'transparent'};
  border-radius: 100px;
`;

const EtcInput = styled.input`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  color: #030303;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  border-radius: 100px;
  border: 1px solid #cbccce;
  box-shadow: 0;
  outline: 0;
  transition: all 200ms ease-in-out;

  &:focus {
    border-color: #246cf6;
    box-shadow: 0px 0px 0px 4px #e9f0fe;
  }

  &::placeholder {
    color: #a7a7a7;
  }
`;

const ContentTitle = styled.strong`
  color: #3a3b3e;
  font-size: 12px;
  font-weight: 700;
  line-height: 150%;
  margin-bottom: 6px;
`;

const Selections = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
`;

const Item = styled.li`
  padding: 5px 8px;
  background-color: #f5f6f7;
  color: #80848a;
  font-size: 12px;
  font-weight: 700;
  line-height: 150%;

  span {
    font-weight: 400;
  }
`;

export default RequestPackageSection;
